/**
 * WarmupTransitionScreen - Transition screen between warmup and test (K Assessment)
 *
 * Displays an encouraging message after the 3 practice questions
 * before starting the timed assessment. Plays audio and auto-advances.
 */

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../hooks/useKAudio';

interface WarmupTransitionScreenProps {
  onComplete: () => void;
  speechEnabled: boolean;
}

const WarmupTransitionScreen: React.FC<WarmupTransitionScreenProps> = ({
  onComplete,
  speechEnabled,
}) => {
  const { playTTSWithCallback } = useKTTS();
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (speechEnabled) {
      // Play the transition audio, then auto-advance after 1 second
      playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.warmup.transition, () => {
        setTimeout(() => {
          onComplete();
        }, 1000);
      }).catch(() => {
        // If audio fails, still advance after a delay
        setTimeout(() => {
          onComplete();
        }, 4000);
      });
    } else {
      // If speech is disabled, show message for 4 seconds then advance
      setTimeout(() => {
        onComplete();
      }, 4000);
    }
  }, [speechEnabled, playTTSWithCallback, onComplete]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col items-center justify-center p-6">
      {/* Star decoration */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="text-8xl mb-8"
      >
        <motion.span
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 10, -10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="inline-block"
        >
          ⭐
        </motion.span>
      </motion.div>

      {/* Message container */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="text-center max-w-2xl"
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
          Great job practicing!
        </h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="space-y-4"
        >
          <p className="text-2xl md:text-3xl text-gray-700">
            Now it's time to show what you know.
          </p>
          <p className="text-2xl md:text-3xl text-gray-700">
            Stay focused. Try your best.
          </p>
        </motion.div>
      </motion.div>

      {/* Animated dots to show something is happening */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-12 flex gap-3"
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2,
            }}
            className="w-4 h-4 rounded-full bg-purple-400"
          />
        ))}
      </motion.div>
    </div>
  );
};

export default WarmupTransitionScreen;
